import { useCallback, useEffect, useRef, useState } from 'react';
import AnimatedLayout from 'components/layouts/animated-layout';
import { Field, Form, FormikProps } from 'formik';
import { motion, useAnimation } from 'framer-motion';
import { immediateToast } from 'utils/toast';
import { useNavigate } from 'react-router-dom';
import { Box, Portal } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useUserProfileQuery } from 'app/store';
import { useAccountsQuery, useAddMutation } from 'features/profile/api/bank.api';
import { variants, getRandomTransformOrigin } from './utils';
import { Styled } from './bank-form.styled';
import { BankCardField } from 'components/formik/bank-card/bank-card-field';
import { handleServerErrors } from 'utils/utils';
import { useRegisterContext } from 'features/register/register.context';

export type BankRegistrationValues = {
    iban: string;
    backup_iban: string;
};

const BankFormTemplate = (
    props: FormikProps<BankRegistrationValues>,
): JSX.Element => {
    const navigate = useNavigate();
    const { bankDraft, setBankDraft } = useRegisterContext();
    const controls = useAnimation();
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const didHydrateBankValues = useRef(false);
    const [addBank, { isLoading: isAdding }] = useAddMutation();
    const { data: banks, isLoading: isFetchingBankList } = useAccountsQuery({});
    const { data: profile, isLoading: isFetchingProfile } = useUserProfileQuery();
    const {
        values,
        setValues,
        isSubmitting,
        setSubmitting,
        setStatus,
        errors,
        validateForm,
        setErrors,
    } = props;

    const fullname = useCallback(
        () =>
            profile?.type === 'person'
                ? `${profile?.name || ''} ${profile?.family || ''}`.trim()
                : profile?.name || '',
        [profile],
    );

    useEffect(() => {
        if (didHydrateBankValues.current) return;

        // Keep the last successfully submitted IBANs in RegisterContext.
        // The registration provider stays mounted while the user moves between
        // steps, so returning quickly from documents must not rehydrate the form
        // from an older RTK Query response that may still be in flight/cached.
        if (bankDraft) {
            didHydrateBankValues.current = true;
            setValues(bankDraft, false);
            setErrors({});
            setShowValidationErrors(false);
            return;
        }

        if (isFetchingBankList) return;

        const items = [...(banks?.items || [])]
            .filter((item) => item?.iban)
            .sort((a, b) => Number(a.id || 0) - Number(b.id || 0));

        didHydrateBankValues.current = true;

        if (!items.length) return;

        const initialValues = {
            iban: items[0]?.iban || '',
            backup_iban: items[1]?.iban || '',
        };

        setBankDraft(initialValues);
        setValues(initialValues, false);
        setErrors({});
        setShowValidationErrors(false);
    }, [
        bankDraft,
        banks?.items,
        isFetchingBankList,
        setBankDraft,
        setErrors,
        setValues,
    ]);


    useEffect(() => {
        setSubmitting(isAdding);
    }, [isAdding, setSubmitting]);

    const handleOnSubmit = async (event: any) => {
        event.preventDefault();

        const validationErrors = await validateForm(values);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setShowValidationErrors(true);
            immediateToast('error', {
                message: 'شماره شبای اصلی و پشتیبان را به‌درستی وارد کنید',
                timeout: 2500,
            });
            controls.start('start');
            return false;
        }

        setShowValidationErrors(false);

        try {
            const submittedValues = {
                iban: values.iban,
                backup_iban: values.backup_iban,
            };

            await addBank({
                ...submittedValues,
                fullname: '',
            }).unwrap();

            // Save the authoritative values from the successful submit before
            // navigating. If the bank list refetch is briefly stale, the next
            // visit to this step hydrates from this draft instead of reverting.
            setBankDraft(submittedValues);

            // The add mutation already invalidates the Banks tag. Let RTK Query
            // refresh in the background; the draft above remains the source of
            // truth for quick step-to-step navigation until a full page reload.
            navigate('/register/documents');
        } catch (error: any) {
            handleServerErrors(error, setStatus);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Form
            id="register-bank-form"
            onSubmit={handleOnSubmit}
            style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}>
            <AnimatedLayout>
                <motion.div
                    style={{ ...getRandomTransformOrigin() }}
                    variants={variants}
                    animate={controls}
                    onAnimationComplete={() => {
                        controls.stop();
                        controls.set('reset');
                    }}>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: 1000,
                            minHeight: { xs: 0, md: 300 },
                            mx: 'auto',
                            py: { xs: 0.5, md: 2 },
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: { xs: 4, md: 4 },
                            alignItems: 'center',
                        }}>
                        <Styled.Container
                            className={
                                showValidationErrors && errors.iban
                                    ? 'has-error'
                                    : ''
                            }>
                            <Field
                                component={BankCardField}
                                fullname={fullname()}
                                subtitle="شماره شبا اصلی"
                                name="iban"
                            />
                            <Styled.BankCard className="fake-bank-card" />
                        </Styled.Container>

                        <Styled.Container
                            className={
                                showValidationErrors && errors.backup_iban
                                    ? 'has-error'
                                    : ''
                            }>
                            <Field
                                component={BankCardField}
                                fullname={fullname()}
                                subtitle="شماره شبا پشتیبان"
                                name="backup_iban"
                            />
                            <Styled.BankCard className="fake-bank-card" />
                        </Styled.Container>
                    </Box>
                </motion.div>
            </AnimatedLayout>

            <Portal
                container={() =>
                    document.getElementById('register-bank-header-action')
                }>
                <LoadingButton
                    variant="contained"
                    loading={
                        isSubmitting ||
                        isAdding ||
                        isFetchingBankList ||
                        isFetchingProfile
                    }
                    type="submit"
                    form="register-bank-form"
                    sx={{ minWidth: { xs: 96, sm: 140 }, minHeight: { xs: 36, sm: 40 } }}>
                    تایید و ادامه
                </LoadingButton>
            </Portal>
        </Form>
    );
};

export default BankFormTemplate;
